const quantityByProduct = new Map();

class EnzoUnitQuantity extends HTMLElement {
  #abortController = new AbortController();
  #variants = new Map();
  #form = null;
  #input = null;
  #productId = '';

  connectedCallback() {
    if (this.#abortController.signal.aborted) this.#abortController = new AbortController();

    this.#productId = this.dataset.productId || '';
    this.#form = this.closest('form');
    this.#input = this.#form?.querySelector('input[name="quantity"]') || null;

    const variantsJson = this.querySelector('[data-enzo-unit-variants]')?.textContent;
    if (variantsJson) {
      try {
        for (const variant of JSON.parse(variantsJson)) {
          this.#variants.set(String(variant.id), Number(variant.price) || 0);
        }
      } catch (error) {
        console.warn('[ENZO] Impossible de lire les prix des variantes.', error);
      }
    }

    if (!this.#form || !this.#input) return;

    const { signal } = this.#abortController;
    this.#form.addEventListener('submit', this.#guardSubmission, { capture: true, signal });
    this.#form.addEventListener('click', this.#guardActionClick, { capture: true, signal });
    this.#form.addEventListener('change', this.#scheduleUpdate, { signal });
    this.addEventListener('quantity-selector:update', this.#handleQuantityUpdate, { signal });
    this.#input.addEventListener('input', this.#update, { signal });
    window.addEventListener('click', this.#guardGlobalAction, { capture: true, signal });

    const savedQuantity = quantityByProduct.get(this.#productId) || 0;
    this.#setQuantity(savedQuantity);
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  #handleQuantityUpdate = (event) => {
    const quantity = Number(event.detail?.quantity) || 0;
    quantityByProduct.set(this.#productId, quantity);
    this.#update();
  };

  #scheduleUpdate = () => {
    window.requestAnimationFrame(this.#update);
  };

  #setQuantity(quantity) {
    if (!this.#input) return;
    this.#input.value = String(quantity);
    const selector = this.#input.closest('quantity-selector-component');
    selector?.updateButtonStates?.();
    quantityByProduct.set(this.#productId, quantity);
    this.#update();
  }

  #getQuantity() {
    return Math.max(0, Number.parseInt(this.#input?.value || '0', 10) || 0);
  }

  #getUnitPrice() {
    const variantId = this.#form?.querySelector('input[name="id"]')?.value;
    return this.#variants.get(String(variantId)) || 0;
  }

  #formatMoney(cents) {
    return (cents / 100).toFixed(2) + ' dh';
  }

  #update = () => {
    const quantity = this.#getQuantity();
    const unitPrice = this.#getUnitPrice();
    const formula = this.querySelector('[data-enzo-unit-formula]');
    const total = this.querySelector('[data-enzo-unit-total]');
    const error = this.querySelector('[data-enzo-unit-error]');

    quantityByProduct.set(this.#productId, quantity);

    if (formula) {
      formula.textContent = quantity > 0
        ? quantity + ' × ' + this.#formatMoney(unitPrice)
        : 'Aucun bracelet sélectionné';
    }
    if (total) total.textContent = this.#formatMoney(unitPrice * quantity);

    if (quantity > 0) {
      error?.setAttribute('hidden', '');
      this.removeAttribute('aria-invalid');
    }
  };

  #isPurchaseAction(target) {
    if (!(target instanceof Element)) return false;
    return Boolean(target.closest(
      '.add-to-cart-button, .accelerated-checkout-block, shopify-accelerated-checkout, .shopify-payment-button, #floatingBuyNowButton, #buttonContainerProduct, #rsi-button-hook, #rsi_buy_now_button, ._rsi-buy-now-button, .rsi_buy_now_button, ._rsi-buy-now-button-app-block-hook, .rsi-btn-skel'
    ));
  }

  #isExternalPurchaseAction(event) {
    const actionSelector = [
      '#floatingBuyNowButton',
      '#buttonContainerProduct',
      '#rsi-button-hook',
      '#rsi_buy_now_button',
      '._rsi-buy-now-button',
      '.rsi_buy_now_button',
      '._rsi-buy-now-button-app-block-hook',
      '.rsi-btn-skel',
    ].join(',');

    for (const node of event.composedPath()) {
      if (!(node instanceof Element)) continue;
      if (node.matches(actionSelector) || node.closest(actionSelector)) return true;

      const control = node.closest('button, a, [role="button"]');
      const label = control?.textContent?.replace(/\s+/g, ' ').trim().toLocaleLowerCase('fr');
      if (label?.includes('acheter maintenant') || label?.includes('paiement à la livraison')) return true;
    }

    return false;
  }

  #guardActionClick = (event) => {
    if (!this.#isPurchaseAction(event.target) || this.#getQuantity() > 0) return;
    this.#stopAndPrompt(event);
  };

  #guardGlobalAction = (event) => {
    if (this.#getQuantity() > 0) return;
    const isStickyAdd = event.target instanceof Element
      && Boolean(event.target.closest('sticky-add-to-cart .add-to-cart-button'));
    if (!isStickyAdd && !this.#isExternalPurchaseAction(event)) return;
    this.#stopAndPrompt(event);
  };

  #guardSubmission = (event) => {
    if (this.#getQuantity() > 0) return;
    this.#stopAndPrompt(event);
  };

  #stopAndPrompt(event) {
    event.preventDefault();
    event.stopImmediatePropagation();

    const error = this.querySelector('[data-enzo-unit-error]');
    error?.removeAttribute('hidden');
    this.setAttribute('aria-invalid', 'true');
    this.classList.remove('is-attention');
    void this.offsetWidth;
    this.classList.add('is-attention');
    this.scrollIntoView({ behavior: 'smooth', block: 'center' });
    window.setTimeout(() => this.#input?.focus({ preventScroll: true }), 450);
  }
}

if (!customElements.get('enzo-unit-quantity')) {
  customElements.define('enzo-unit-quantity', EnzoUnitQuantity);
}